/**
 * Databricks Table Browser (DC-1164)
 *
 * Hierarchical browser for Unity Catalog:
 * - Catalogs → Schemas → Tables
 * - Table preview with sample data
 * - Integration with FAIR Compass analysis
 *
 * @see Documentation/Development/Design Docs/Sprint 13/Unified_Integration_Architecture.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BRANDING } from '../../config/branding';
import { useAuth } from '../../hooks/useAuth';
import './DatabricksTableBrowser.css';

// ============================================================================
// TYPES
// ============================================================================

interface Catalog {
  name: string;
  owner?: string;
  comment?: string;
}

interface Schema {
  name: string;
  catalog_name: string;
  owner?: string;
  comment?: string;
}

interface Table {
  name: string;
  catalog_name: string;
  schema_name: string;
  table_type: 'MANAGED' | 'EXTERNAL' | 'VIEW';
  data_source_format?: string;
  comment?: string;
}

interface Column {
  name: string;
  type_text: string;
  nullable: boolean;
  comment?: string;
}

interface TablePreview {
  columns: Array<{ name: string; type: string }>;
  rows: unknown[][];
  rowCount: number;
  totalRowCount: number;
}

interface SelectedTable {
  catalog: string;
  schema: string;
  table: string;
}

export type WarehouseState = 'RUNNING' | 'STOPPED' | 'STARTING' | 'STOPPING' | 'DELETING' | 'DELETED' | 'UNKNOWN';

interface DatabricksTableBrowserProps {
  connectionId: string;
  onTableSelect?: (table: SelectedTable, columns: Column[]) => void;
  onAnalyze?: (table: SelectedTable) => void;
  onWarehouseStatus?: (state: WarehouseState) => void;
  showAnalyzeButton?: boolean;
  /**
   * When true, render checkboxes on each table row and emit the full
   * selection set via onSelectionChange. The single-table details panel
   * is hidden — parents render their own bulk-action UI.
   * Default: false (single-select behavior, unchanged).
   */
  multiSelect?: boolean;
  onSelectionChange?: (tables: SelectedTable[]) => void;
}

/** Stable key for a SelectedTable entry. */
function tableKey(t: SelectedTable): string {
  return `${t.catalog}.${t.schema}.${t.table}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const DatabricksTableBrowser: React.FC<DatabricksTableBrowserProps> = ({
  connectionId,
  onTableSelect,
  onAnalyze,
  onWarehouseStatus,
  showAnalyzeButton = true,
  multiSelect = false,
  onSelectionChange,
}) => {
  const { accessToken } = useAuth();

  // State
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [schemas, setSchemas] = useState<Record<string, Schema[]>>({});
  const [tables, setTables] = useState<Record<string, Table[]>>({});
  const [columns, setColumns] = useState<Record<string, Column[]>>({});
  const [preview, setPreview] = useState<TablePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [warehouseState, setWarehouseState] = useState<WarehouseState>('UNKNOWN');

  // Expansion state
  const [expandedCatalogs, setExpandedCatalogs] = useState<Set<string>>(new Set());
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(new Set());
  const [selectedTable, setSelectedTable] = useState<SelectedTable | null>(null);
  // Multi-select state. Keyed by `catalog.schema.table` so we can do O(1)
  // membership tests when rendering checkboxes.
  const [selectedTables, setSelectedTables] = useState<Map<string, SelectedTable>>(new Map());

  // Loading state
  const [loading, setLoading] = useState<{
    catalogs: boolean;
    schemas: Record<string, boolean>;
    tables: Record<string, boolean>;
    preview: boolean;
  }>({
    catalogs: false,
    schemas: {},
    tables: {},
    preview: false,
  });

  const [error, setError] = useState<string | null>(null);

  // ==========================================================================
  // API CALLS
  // ==========================================================================

  const apiBaseUrl = `/api/integrations/databricks`;

  const fetchCatalogs = useCallback(async () => {
    if (!accessToken) return;

    setLoading((prev) => ({ ...prev, catalogs: true }));
    setError(null);

    try {
      const res = await fetch(`${apiBaseUrl}/catalogs?connectionId=${connectionId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) throw new Error('Failed to fetch catalogs');

      const data = await res.json();
      setCatalogs(data.catalogs || []);
    } catch (err) {
      console.error('Error fetching catalogs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load catalogs');
    } finally {
      setLoading((prev) => ({ ...prev, catalogs: false }));
    }
  }, [accessToken, connectionId, apiBaseUrl]);

  const fetchSchemas = useCallback(
    async (catalogName: string) => {
      if (!accessToken) return;

      setLoading((prev) => ({
        ...prev,
        schemas: { ...prev.schemas, [catalogName]: true },
      }));

      try {
        const res = await fetch(
          `${apiBaseUrl}/catalogs/${catalogName}/schemas?connectionId=${connectionId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!res.ok) throw new Error('Failed to fetch schemas');

        const data = await res.json();
        setSchemas((prev) => ({ ...prev, [catalogName]: data.schemas || [] }));
      } catch (err) {
        console.error('Error fetching schemas:', err);
        setError(err instanceof Error ? err.message : 'Failed to load schemas');
      } finally {
        setLoading((prev) => ({
          ...prev,
          schemas: { ...prev.schemas, [catalogName]: false },
        }));
      }
    },
    [accessToken, connectionId, apiBaseUrl]
  );

  const fetchTables = useCallback(
    async (catalogName: string, schemaName: string) => {
      if (!accessToken) return;

      const key = `${catalogName}.${schemaName}`;
      setLoading((prev) => ({
        ...prev,
        tables: { ...prev.tables, [key]: true },
      }));

      try {
        const res = await fetch(
          `${apiBaseUrl}/catalogs/${catalogName}/schemas/${schemaName}/tables?connectionId=${connectionId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!res.ok) throw new Error('Failed to fetch tables');

        const data = await res.json();
        setTables((prev) => ({ ...prev, [key]: data.tables || [] }));
      } catch (err) {
        console.error('Error fetching tables:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tables');
      } finally {
        setLoading((prev) => ({
          ...prev,
          tables: { ...prev.tables, [key]: false },
        }));
      }
    },
    [accessToken, connectionId, apiBaseUrl]
  );

  const fetchTableDetails = useCallback(
    async (catalogName: string, schemaName: string, tableName: string) => {
      if (!accessToken) return;

      const fullPath = `${catalogName}.${schemaName}.${tableName}`;
      setLoading((prev) => ({ ...prev, preview: true }));
      setPreviewError(null);
      // DC-1812: clear the OUTGOING table's preview before fetching the next
      // one. Only `previewError` was reset here, so the previous table's rows
      // stayed on screen for the whole fetch and — if the metadata request
      // threw — survived in the catch block, rendering one table's data under
      // another table's name. Showing nothing while we do not know is the
      // honest state; showing the last table we did know about is not.
      //
      // `columns` is deliberately NOT cleared: it is keyed by full path, so
      // it cannot bleed between tables the way a single preview slot can.
      setPreview(null);

      try {
        // Fetch table metadata
        const tableRes = await fetch(
          `${apiBaseUrl}/catalogs/${catalogName}/schemas/${schemaName}/tables/${tableName}?connectionId=${connectionId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (!tableRes.ok) throw new Error('Failed to fetch table details');

        const tableData = await tableRes.json();
        setColumns((prev) => ({ ...prev, [fullPath]: tableData.table?.columns || [] }));

        // Fetch preview data
        const previewRes = await fetch(
          `${apiBaseUrl}/catalogs/${catalogName}/schemas/${schemaName}/tables/${tableName}/preview?connectionId=${connectionId}&limit=50`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        if (previewRes.ok) {
          const previewData = await previewRes.json();
          setPreview(previewData);
          setPreviewError(null);
        } else {
          setPreview(null);
          setPreviewError(
            'Data preview unavailable — the table metadata loaded, but the sample query failed (check that the SQL Warehouse is running).'
          );
        }

        // Notify parent
        if (onTableSelect && tableData.table?.columns) {
          onTableSelect(
            { catalog: catalogName, schema: schemaName, table: tableName },
            tableData.table.columns
          );
        }
      } catch (err) {
        console.error('Error fetching table details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load table details');
      } finally {
        setLoading((prev) => ({ ...prev, preview: false }));
      }
    },
    [accessToken, connectionId, apiBaseUrl, onTableSelect]
  );

  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  // Fetch warehouse status on mount and poll every 15s if not RUNNING
  const fetchWarehouseStatus = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(
        `${apiBaseUrl}/warehouse-status?connectionId=${connectionId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const state = (data.state || 'UNKNOWN') as WarehouseState;
        setWarehouseState(state);
        onWarehouseStatus?.(state);
      }
    } catch {
      // Non-critical, but don't show a stale state as current — degrade to UNKNOWN
      setWarehouseState('UNKNOWN');
      onWarehouseStatus?.('UNKNOWN');
    }
  }, [accessToken, connectionId, apiBaseUrl, onWarehouseStatus]);

  useEffect(() => {
    fetchWarehouseStatus();
    const interval = setInterval(fetchWarehouseStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchWarehouseStatus]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleCatalogToggle = (catalogName: string) => {
    const newExpanded = new Set(expandedCatalogs);
    if (newExpanded.has(catalogName)) {
      newExpanded.delete(catalogName);
    } else {
      newExpanded.add(catalogName);
      // Fetch schemas if not loaded
      if (!schemas[catalogName]) {
        fetchSchemas(catalogName);
      }
    }
    setExpandedCatalogs(newExpanded);
  };

  const handleSchemaToggle = (catalogName: string, schemaName: string) => {
    const key = `${catalogName}.${schemaName}`;
    const newExpanded = new Set(expandedSchemas);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
      // Fetch tables if not loaded
      if (!tables[key]) {
        fetchTables(catalogName, schemaName);
      }
    }
    setExpandedSchemas(newExpanded);
  };

  const handleTableSelect = (catalogName: string, schemaName: string, tableName: string) => {
    const newSelected = { catalog: catalogName, schema: schemaName, table: tableName };
    if (multiSelect) {
      // Toggle membership in the selection set; don't fetch column details
      // (the bulk path doesn't need them per-table).
      const key = tableKey(newSelected);
      setSelectedTables((prev) => {
        const next = new Map(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, newSelected);
        }
        onSelectionChange?.(Array.from(next.values()));
        return next;
      });
      return;
    }
    setSelectedTable(newSelected);
    fetchTableDetails(catalogName, schemaName, tableName);
  };

  /**
   * Toggle every visible table in a schema (multi-select only).
   * If all visible tables are already selected, deselect them; else select all.
   */
  const handleSchemaSelectAll = (catalogName: string, schemaName: string): void => {
    if (!multiSelect) return;
    const schemaKey = `${catalogName}.${schemaName}`;
    const visible = tables[schemaKey] ?? [];
    if (visible.length === 0) return;

    setSelectedTables((prev) => {
      const next = new Map(prev);
      const allSelected = visible.every((t) =>
        next.has(tableKey({ catalog: catalogName, schema: schemaName, table: t.name })),
      );
      for (const t of visible) {
        const entry = { catalog: catalogName, schema: schemaName, table: t.name };
        const key = tableKey(entry);
        if (allSelected) {
          next.delete(key);
        } else {
          next.set(key, entry);
        }
      }
      onSelectionChange?.(Array.from(next.values()));
      return next;
    });
  };

  const handleAnalyze = () => {
    if (selectedTable && onAnalyze) {
      onAnalyze(selectedTable);
    }
  };

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  const TABLE_TYPE_ICONS: Record<string, string> = {
    MANAGED: '\uD83D\uDCCB',
    EXTERNAL: '\uD83D\uDD17',
    VIEW: '\uD83D\uDC41\uFE0F',
  };

  const getTableTypeIcon = (tableType: string): string => {
    return TABLE_TYPE_ICONS[tableType] ?? '\uD83D\uDCC4';
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="databricks-table-browser">
      {/* Tree View */}
      <div className="browser-tree">
        <div className="tree-header">
          <span>Unity Catalog</span>
          <span
            className={`warehouse-status-badge warehouse-${warehouseState.toLowerCase()}`}
            title={`SQL Warehouse: ${warehouseState}`}
          >
            <span className="warehouse-dot" />
            {warehouseState === 'RUNNING' ? 'Warehouse Running' :
             warehouseState === 'STOPPED' ? 'Warehouse Stopped' :
             warehouseState === 'STARTING' ? 'Warehouse Starting...' :
             warehouseState === 'STOPPING' ? 'Warehouse Stopping...' :
             ''}
          </span>
          <button
            className="refresh-btn"
            onClick={fetchCatalogs}
            disabled={loading.catalogs}
            title="Refresh catalogs"
            aria-label="Refresh catalogs"
          >
            🔄
          </button>
        </div>

        {error && (
          <div className="browser-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading.catalogs ? (
          <div className="loading-indicator">Loading catalogs...</div>
        ) : (
          <ul className="tree-root">
            {catalogs.map((catalog) => (
              <li key={catalog.name} className="tree-item catalog">
                <div
                  className="tree-node"
                  onClick={() => handleCatalogToggle(catalog.name)}
                >
                  <span className="expand-icon">
                    {expandedCatalogs.has(catalog.name) ? '▼' : '▶'}
                  </span>
                  <span className="node-icon">📚</span>
                  <span className="node-name">{catalog.name}</span>
                </div>

                {expandedCatalogs.has(catalog.name) && (
                  <ul className="tree-children">
                    {loading.schemas[catalog.name] ? (
                      <li className="loading-item">Loading schemas...</li>
                    ) : (
                      (schemas[catalog.name] || []).map((schema) => {
                        const schemaKey = `${catalog.name}.${schema.name}`;
                        return (
                          <li key={schema.name} className="tree-item schema">
                            <div
                              className="tree-node"
                              onClick={() => handleSchemaToggle(catalog.name, schema.name)}
                            >
                              <span className="expand-icon">
                                {expandedSchemas.has(schemaKey) ? '▼' : '▶'}
                              </span>
                              {multiSelect && (tables[schemaKey]?.length ?? 0) > 0 && (() => {
                                const visible = tables[schemaKey] ?? [];
                                const allSelected = visible.every((t) =>
                                  selectedTables.has(tableKey({ catalog: catalog.name, schema: schema.name, table: t.name })),
                                );
                                const someSelected = !allSelected && visible.some((t) =>
                                  selectedTables.has(tableKey({ catalog: catalog.name, schema: schema.name, table: t.name })),
                                );
                                return (
                                  <input
                                    type="checkbox"
                                    className="bulk-select-checkbox"
                                    checked={allSelected}
                                    ref={(el) => {
                                      if (el) el.indeterminate = someSelected;
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => handleSchemaSelectAll(catalog.name, schema.name)}
                                    aria-label={`Select all tables in ${schema.name}`}
                                  />
                                );
                              })()}
                              <span className="node-icon">📁</span>
                              <span className="node-name">{schema.name}</span>
                            </div>

                            {expandedSchemas.has(schemaKey) && (
                              <ul className="tree-children">
                                {loading.tables[schemaKey] ? (
                                  <li className="loading-item">Loading tables...</li>
                                ) : (
                                  (tables[schemaKey] || []).map((table) => {
                                    const entry = { catalog: catalog.name, schema: schema.name, table: table.name };
                                    const inBulkSelection = selectedTables.has(tableKey(entry));
                                    const isSingleSelected =
                                      !multiSelect &&
                                      selectedTable?.catalog === catalog.name &&
                                      selectedTable?.schema === schema.name &&
                                      selectedTable?.table === table.name;
                                    const isSelected = multiSelect ? inBulkSelection : isSingleSelected;

                                    return (
                                      <li
                                        key={table.name}
                                        className={`tree-item table ${isSelected ? 'selected' : ''}`}
                                      >
                                        <div
                                          className="tree-node"
                                          onClick={() =>
                                            handleTableSelect(catalog.name, schema.name, table.name)
                                          }
                                        >
                                          <span className="expand-icon" />
                                          {multiSelect && (
                                            <input
                                              type="checkbox"
                                              className="bulk-select-checkbox"
                                              checked={inBulkSelection}
                                              onClick={(e) => e.stopPropagation()}
                                              onChange={() =>
                                                handleTableSelect(catalog.name, schema.name, table.name)
                                              }
                                              aria-label={`Select ${table.name}`}
                                            />
                                          )}
                                          <span className="node-icon">
                                            {getTableTypeIcon(table.table_type)}
                                          </span>
                                          <span className="node-name">{table.name}</span>
                                          <span className="table-type">{table.table_type}</span>
                                        </div>
                                      </li>
                                    );
                                  })
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Table Details Panel */}
      <div className="browser-details">
        {multiSelect ? (
          <div className="details-empty">
            <p style={{ fontSize: '1.1rem', fontWeight: 500, color: 'var(--tri-ink)', margin: 0 }}>
              {selectedTables.size === 0
                ? 'Select tables to bulk-analyze'
                : `${selectedTables.size} table${selectedTables.size === 1 ? '' : 's'} selected`}
            </p>
            {selectedTables.size > 0 && (
              <p style={{ fontSize: '0.85rem', color: 'var(--tri-body)', marginTop: '0.5rem' }}>
                The parent component renders the &quot;Run FAIR on selected&quot; action.
              </p>
            )}
          </div>
        ) : !selectedTable ? (
          <div className="details-empty">
            <p>Select a table to view details</p>
          </div>
        ) : loading.preview ? (
          <div className="details-loading">
            <div className="loading-spinner" />
            <p>Loading table details...</p>
          </div>
        ) : (
          <>
            <div className="details-header">
              <h3>
                {selectedTable.catalog}.{selectedTable.schema}.{selectedTable.table}
              </h3>
              {showAnalyzeButton && onAnalyze && (
                <button className="analyze-btn" onClick={handleAnalyze}>
                  {`Analyze with ${BRANDING.productName}`}
                </button>
              )}
            </div>

            {/* Columns */}
            <div className="details-section">
              <h4>Columns ({columns[`${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`]?.length || 0})</h4>
              <table className="columns-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Nullable</th>
                  </tr>
                </thead>
                <tbody>
                  {(columns[`${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`] || []).map(
                    (col) => (
                      <tr key={col.name}>
                        <td>{col.name}</td>
                        <td className="type-cell">{col.type_text}</td>
                        <td>{col.nullable ? 'Yes' : 'No'}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            {/* Preview */}
            {previewError && !preview && (
              <div className="details-section">
                <h4>Data Preview</h4>
                <p role="status" style={{ fontSize: '0.85rem', color: 'var(--tri-body)' }}>{previewError}</p>
              </div>
            )}
            {preview && (
              <div className="details-section">
                <h4>
                  Data Preview ({preview.rowCount} of {preview.totalRowCount} rows)
                </h4>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        {preview.columns.map((col) => (
                          <th key={col.name}>{col.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row, idx) => (
                        <tr key={idx}>
                          {row.map((cell, cellIdx) => (
                            <td key={cellIdx}>
                              {cell === null ? (
                                <span className="null-value">NULL</span>
                              ) : (
                                String(cell)
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DatabricksTableBrowser;
